import { Box, Link } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import {
    LabelText,
    labelValueGridSx,
    ValueCell,
    ValueText,
    WrappingValueText,
} from '~/components/Fields/LabeledValue';
import EditablePropertyValue from '~/components/Fields/EditablePropertyValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { ApprovalStatusLabel } from '~/components/Structures/ApprovalMark';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import RoiColor from '~/components/Structures/RoiColor';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { sopClassName, StorageSOPClassUID } from '~/domain/dicom/modality';
import { RoiCategory } from '~/domain/patient/roi';
import { AvailableStructureSetView } from '~/domain/projections/views';
import { useRoiListView } from '~/hooks/domain/useRoiListView';
import { useStructureSetDetailView } from '~/hooks/domain/useStructureSetDetailView';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { formatReviewDateTime } from '~/utils/date';
import { describeGenerationModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { formatMmAsCmAtScale } from '~/utils/units';
import ProvenanceFields from '~/components/Provenance/ProvenanceFields';
import IdentitySection from './IdentitySection';
import { useIsInObjectDetails } from './objectDetailsContext';
import PropertySection, {
    CompactRow,
    CountedRowGrid,
    PropertySectionColumns,
} from './PropertySection';
import PropertiesPanel from './PropertiesPanel';

/** Shown for a value the set does not hold. */
const DASH = '—';

/** How the user edits one of the set's text values from the list. */
export interface StructureSetTextEdit {
    /** Whether the value can be edited now; a pencil ends it only then. */
    editable: boolean;
    /** Whether the value's editor is open. */
    editing: boolean;
    onEditingChange: (editing: boolean) => void;
    onSubmit: (value: string) => void;
    /** The reason a value cannot be used, or null. */
    validate: (value: string) => string | null;
    /**
     * Keep the line breaks the value carries, for a DICOM VR ST value. See
     * InlineEdit.
     */
    multiline?: boolean;
}

/**
 * A text value of the list, masked with the privacy toggle, then the pencil
 * that opens its editor in place while `edit` allows it and patient info is
 * shown.
 */
function EditableTextValue({
    value,
    edit,
    patientInfoShown,
    ariaLabel,
    'data-testid': testId,
}: {
    value: string;
    edit: StructureSetTextEdit | undefined;
    /**
     * While false the value offers no editor: an editor prints the value in
     * plaintext, past the mask.
     */
    patientInfoShown: boolean;
    ariaLabel: string;
    'data-testid': string;
}) {
    const display = (
        <WrappingValueText component="span">
            <SensitiveInfoTypography>{value || DASH}</SensitiveInfoTypography>
        </WrappingValueText>
    );
    if (!edit) {
        return <ValueCell>{display}</ValueCell>;
    }
    return (
        <EditablePropertyValue
            display={display}
            editable={edit.editable && patientInfoShown}
            editing={edit.editing}
            onEditingChange={edit.onEditingChange}
            seed={value}
            onSubmit={edit.onSubmit}
            validate={edit.validate}
            multiline={edit.multiline}
            editLabel={ariaLabel}
            fieldLabel={ariaLabel}
            fontWeight={500}
            buttonTestId={testId}
        />
    );
}

/** Breakdown order: what the user drew first, supports and leftovers last. */
const CATEGORY_ORDER: readonly RoiCategory[] = [
    'Target',
    'OAR',
    'External',
    'Marker',
    'Support',
    'Other',
];

/**
 * Structures shown before "Show all": five two-column rows, so the
 * dialog opens without a scrollbar.
 */
const STRUCTURES_PREVIEW_COUNT = 10;

interface StructureSetPropertiesProps {
    structureSet: AvailableStructureSetView;
    /** Opens the set's DICOM tags; a hover panel's route to them. */
    onViewDicom?: () => void;
    onDelete?: () => void;
    /** How the label edits in place, where the list offers that. */
    labelEdit?: StructureSetTextEdit;
    /** How the description edits in place, where the list offers that. */
    descriptionEdit?: StructureSetTextEdit;
}

/**
 * The properties of a structure set: its label, description and approval
 * status, then its content, the image it is drawn on, how it was made and
 * its identity. The label and the description end with a pencil while
 * they can be edited, and edit in place. Both are masked with the privacy
 * toggle and offer no editor while it hides patient info, since free text
 * can name the patient.
 */
export default function StructureSetProperties({
    structureSet,
    onViewDicom,
    onDelete,
    labelEdit,
    descriptionEdit,
}: StructureSetPropertiesProps) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const detail = useStructureSetDetailView(structureSet.structureSetId);
    const roiList = useRoiListView(structureSet.structureSetId);
    const [showAllStructures, setShowAllStructures] = useState(false);
    const inDetailsDialog = useIsInObjectDetails();
    // The structures list takes the dialog's width and a click on "Show
    // all"; a hover panel offers neither, and its row already counts them.
    const listStructures = inDetailsDialog && roiList.length > 0;

    const approvedRoiCount = useMemo(
        () => roiList.filter((roi) => roi.isApproved).length,
        [roiList]
    );
    const categoryBreakdown = useMemo(() => {
        const counts = new Map<RoiCategory, number>();
        for (const roi of roiList) {
            counts.set(roi.category, (counts.get(roi.category) ?? 0) + 1);
        }
        return CATEGORY_ORDER.filter((category) => counts.has(category))
            .map(
                (category) =>
                    `${counts.get(category)} ${intl.formatMessage({
                        id: `roiCategory.${category}`,
                    })}`
            )
            .join(' / ');
    }, [roiList, intl]);

    const imageLabel = structureSet.imageLabel ?? detail?.imageLabel;
    const imageValue =
        [detail?.imageModality, imageLabel].filter(Boolean).join(' / ') ||
        undefined;
    const generationModel = detail?.generationModel;
    const modelValue =
        describeGenerationModel(generationModel)?.model ??
        detail?.manufacturerModelName;

    return (
        <PropertiesPanel
            onViewDicom={onViewDicom}
            onDelete={onDelete}
            identity={
                <IdentitySection
                    rows={[
                        {
                            label: intl.formatMessage({ id: 'sopClass' }),
                            value: sopClassName(
                                StorageSOPClassUID.RTStructureSetStorage
                            ),
                            note: StorageSOPClassUID.RTStructureSetStorage,
                        },
                        {
                            label: intl.formatMessage({ id: 'sopInstanceUid' }),
                            id: structureSet.sopInstanceUid,
                        },
                        {
                            label: intl.formatMessage({
                                id: 'properties.referencedSeries',
                            }),
                            // The DICOM reference first: it is known even
                            // while the image it names is not loaded.
                            id:
                                detail?.referencedSeriesUid ||
                                detail?.imageSeriesUid ||
                                structureSet.imageSeriesUid,
                            note: imageValue && (
                                <SensitiveInfoTypography>
                                    {imageValue}
                                </SensitiveInfoTypography>
                            ),
                        },
                        {
                            label: intl.formatMessage({
                                id: 'frameOfReferenceUid',
                            }),
                            id: detail?.frameOfReferenceUid,
                        },
                    ]}
                />
            }
            trailing={
                listStructures ? (
                    <CountedRowGrid
                        id="structures"
                        count={roiList.length}
                        footer={
                            roiList.length > STRUCTURES_PREVIEW_COUNT && (
                                <Link
                                    component="button"
                                    underline="hover"
                                    onClick={() =>
                                        setShowAllStructures((show) => !show)
                                    }
                                    sx={{ fontSize: 11, mt: 0.75 }}
                                    data-testid="structures-show-all"
                                >
                                    <FormattedMessage
                                        id={
                                            showAllStructures
                                                ? 'structures.showLess'
                                                : 'structures.showAll'
                                        }
                                        values={{ count: roiList.length }}
                                    />
                                </Link>
                            )
                        }
                    >
                        {(showAllStructures
                            ? roiList
                            : roiList.slice(0, STRUCTURES_PREVIEW_COUNT)
                        ).map((roi) => (
                            <CompactRow
                                key={roi.id}
                                leading={
                                    <RoiColor
                                        color={roi.color}
                                        size={8}
                                        isEmptyVolume={roi.isEmpty}
                                        isMarker={roi.category === 'Marker'}
                                        isTarget={roi.category === 'Target'}
                                    />
                                }
                                trailing={
                                    <ReviewStatus
                                        status={
                                            roi.isApproved
                                                ? DICOMApprovalStatus.APPROVED
                                                : DICOMApprovalStatus.UNAPPROVED
                                        }
                                        fontSize={10}
                                        color="text.secondary"
                                    />
                                }
                            >
                                {roi.name}
                            </CompactRow>
                        ))}
                    </CountedRowGrid>
                ) : undefined
            }
            layout="sections"
        >
            <Box sx={{ ...labelValueGridSx, columnGap: '10px' }}>
                <LabelText>
                    <FormattedMessage id="label" />
                </LabelText>
                <EditableTextValue
                    value={structureSet.displayName}
                    edit={labelEdit}
                    patientInfoShown={showSensitiveInfo}
                    ariaLabel={intl.formatMessage({ id: 'rename' })}
                    data-testid="structure-set-properties-rename-button"
                />

                <LabelText>
                    <FormattedMessage id="description" />
                </LabelText>
                <EditableTextValue
                    value={detail?.description ?? ''}
                    // Until the set's detail is read, an editor would open
                    // empty, and saving it would overwrite a description
                    // the user never saw.
                    edit={
                        descriptionEdit && {
                            ...descriptionEdit,
                            editable: descriptionEdit.editable && !!detail,
                        }
                    }
                    patientInfoShown={showSensitiveInfo}
                    ariaLabel={intl.formatMessage({
                        id: 'datasetPicker.editDescription',
                    })}
                    data-testid="structure-set-properties-description-edit-button"
                />

                {/* Whether the set itself is approved decides whether it can
                    be edited at all. The dialog states it in its header; a
                    hover panel or the side panel has none. */}
                {detail && !inDetailsDialog && (
                    <>
                        <LabelText>
                            <FormattedMessage id="approvalStatus" />
                        </LabelText>
                        <ValueCell>
                            <ValueText component="span" display="flex">
                                <ApprovalStatusLabel
                                    status={detail.approvalStatus}
                                    size={14}
                                    data-testid="structure-set-properties-approval-value"
                                />
                            </ValueText>
                        </ValueCell>
                    </>
                )}
            </Box>

            <PropertySectionColumns>
                {detail && (
                    <PropertySection id="properties.content">
                        <LabelText>
                            <FormattedMessage id="structures" />
                        </LabelText>
                        <ValueText component="span">
                            {detail.numberOfStructures}
                        </ValueText>

                        {roiList.length > 0 && (
                            <>
                                <LabelText>
                                    <FormattedMessage id="approved" />
                                </LabelText>
                                <ValueText component="span">
                                    {`${approvedRoiCount} / ${roiList.length}`}
                                </ValueText>

                                <LabelText>
                                    <FormattedMessage id="properties.breakdown" />
                                </LabelText>
                                <WrappingValueText
                                    component="span"
                                    data-testid="structure-set-breakdown-value"
                                >
                                    {categoryBreakdown || DASH}
                                </WrappingValueText>
                            </>
                        )}
                    </PropertySection>
                )}

                {imageValue && (
                    <PropertySection id="structure.drawnOn">
                        <LabelText>
                            <FormattedMessage id="registration.sourceImage" />
                        </LabelText>
                        <ValueText component="span">
                            <SensitiveInfoTypography>
                                {imageValue}
                            </SensitiveInfoTypography>
                        </ValueText>

                        {detail?.imageSliceSpacingMm != null && (
                            <>
                                <LabelText>
                                    <FormattedMessage id="slice.sampling.between" />{' '}
                                    (cm)
                                </LabelText>
                                <ValueText component="span">
                                    {formatMmAsCmAtScale(
                                        detail.imageSliceSpacingMm,
                                        'voxel'
                                    )}
                                </ValueText>
                            </>
                        )}

                        {detail?.imageSliceCount != null && (
                            <>
                                <LabelText>
                                    <FormattedMessage id="slices" />
                                </LabelText>
                                <ValueText component="span">
                                    {detail.imageSliceCount}
                                </ValueText>
                            </>
                        )}
                    </PropertySection>
                )}

                {detail && (
                    <PropertySection id="properties.provenance">
                        <ProvenanceFields
                            recorded={detail.recordedOrigin}
                            sessionModel={generationModel}
                            structures={detail.structureGeneration}
                        />

                        <LabelText>
                            <FormattedMessage id="manufacturer" />
                        </LabelText>
                        <ValueText component="span">
                            {detail.manufacturer || DASH}
                        </ValueText>

                        <LabelText>
                            <FormattedMessage id="manufacturerModelName" />
                        </LabelText>
                        <WrappingValueText component="span">
                            {modelValue || DASH}
                        </WrappingValueText>

                        {/* A recorded origin already states the customization. */}
                        {!detail.recordedOrigin &&
                            generationModel?.customizationName && (
                                <>
                                    <LabelText>
                                        <FormattedMessage id="properties.modelProfile" />
                                    </LabelText>
                                    <ValueText component="span">
                                        {generationModel.customizationName}
                                    </ValueText>
                                </>
                            )}

                        <LabelText>
                            <FormattedMessage id="softwareVersions" />
                        </LabelText>
                        <WrappingValueText component="span">
                            {detail.softwareVersions || DASH}
                        </WrappingValueText>

                        {!!detail.reviewerName && (
                            <>
                                <LabelText>
                                    <FormattedMessage id="reviewerName" />
                                </LabelText>
                                <ValueText component="span">
                                    <SensitiveInfoTypography>
                                        {detail.reviewerName}
                                    </SensitiveInfoTypography>
                                </ValueText>

                                <LabelText>
                                    <FormattedMessage id="reviewTime" />
                                </LabelText>
                                <ValueText component="span">
                                    <SensitiveInfoTypography>
                                        {formatReviewDateTime(
                                            detail.reviewDate,
                                            detail.reviewTime
                                        )}
                                    </SensitiveInfoTypography>
                                </ValueText>
                            </>
                        )}
                    </PropertySection>
                )}
            </PropertySectionColumns>
        </PropertiesPanel>
    );
}
